/**
 * Set and drift, and course and speed made good. Both compare two points over a
 * time interval, so they share one sheet with a toggle.
 *
 *   - Set and drift: tap the DR, then the observed fix, give the interval. The
 *     app shows the tide the boat actually felt (the inverse of the EP).
 *   - Made good: tap two positions, give the interval, and read the course and
 *     speed made good over the ground.
 *
 * It reuses the shell's mark-pick mechanism, asking it to reopen this sheet.
 */
import { useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useObjectsStore, nextObjectId } from '@/store/objects';
import { buildSetDrift, buildMadeGood } from '@/chartwork';
import { formatBearing, formatDistance } from '@/nav';
import { Sheet } from './Sheet';

type Calc = 'setdrift' | 'madegood';

export function DerivedSheet() {
  const pendingMark = useShellStore((s) => s.pendingMark);
  const armFixPick = useShellStore((s) => s.armFixPick);
  const setPendingMark = useShellStore((s) => s.setPendingMark);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  const [calc, setCalc] = useState<Calc>('setdrift');
  const [pointA, setPointA] = useState<[number, number] | null>(null);
  const [hours, setHours] = useState('1');

  const firstLabel = calc === 'setdrift' ? 'the DR' : 'the first point';
  const secondLabel = calc === 'setdrift' ? 'the fix' : 'the second point';

  function reset() {
    setPointA(null);
    setHours('1');
    setPendingMark(null);
  }

  function compute() {
    const h = Number.parseFloat(hours);
    if (!pointA || !pendingMark || Number.isNaN(h)) {
      showToast('Pick both points and the time');
      return;
    }
    const time = { iso: new Date().toISOString() };
    if (calc === 'setdrift') {
      const built = buildSetDrift({ from: pointA, to: pendingMark.position, hours: h, time, newId: nextObjectId });
      useObjectsStore.getState().putObjects([built.drPoint, built.fixPoint, built.vector]);
      showToast(
        `Set ${formatBearing(built.result.setDeg)}, drift ${formatDistance(built.result.driftNm)}, ${built.result.rateKnots.toFixed(1)} kn`,
      );
    } else {
      const built = buildMadeGood({ from: pointA, to: pendingMark.position, hours: h, time, newId: nextObjectId });
      useObjectsStore.getState().putObjects([built.fromPoint, built.toPoint, built.vector]);
      showToast(
        `CMG ${formatBearing(built.result.cmgDeg)}, SMG ${built.result.smgKnots.toFixed(1)} kn`,
      );
    }
    reset();
    closeSheet();
  }

  return (
    <Sheet
      name="derived"
      title="Set & drift, made good"
      sub="Compare two positions over a time interval. Set and drift shows the tide the boat actually felt; made good shows the course and speed achieved over the ground."
    >
      <div className="acp-seg">
        <button type="button" className={calc === 'setdrift' ? 'on' : ''} onClick={() => { setCalc('setdrift'); reset(); }}>
          Set &amp; drift
        </button>
        <button type="button" className={calc === 'madegood' ? 'on' : ''} onClick={() => { setCalc('madegood'); reset(); }}>
          CMG / SMG
        </button>
      </div>

      <div className="acp-kv">
        <span className="k">First point ({firstLabel})</span>
        <span className="v">{pointA ? 'set' : '—'}</span>
      </div>

      {!pointA ? (
        pendingMark ? (
          <div className="acp-btn-row">
            <button type="button" className="acp-btn primary" onClick={() => { setPointA(pendingMark.position); setPendingMark(null); }}>
              Use as first point
            </button>
          </div>
        ) : (
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn primary"
              onClick={() => { armFixPick('derived'); showToast(`Tap ${firstLabel}`); }}
            >
              Pick first point
            </button>
          </div>
        )
      ) : (
        <>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-soft)', marginTop: 10 }}>
            Time interval, hours
            <input
              className="acp-note-input"
              style={{ marginTop: 2 }}
              inputMode="decimal"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </label>
          {pendingMark ? (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn" onClick={reset}>
                Start over
              </button>
              <button type="button" className="acp-btn primary" onClick={compute}>
                Compute
              </button>
            </div>
          ) : (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn" onClick={reset}>
                Start over
              </button>
              <button
                type="button"
                className="acp-btn primary"
                onClick={() => { armFixPick('derived'); showToast(`Tap ${secondLabel}`); }}
              >
                Pick second point
              </button>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}
